import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
interface CalendarEvent {
  id: number | string;
  title: string;
  date: string; // YYYY-MM-DD
  type: string;
  priority?: 'high' | 'medium' | 'low';
  client?: string;
}
interface CalendarViewProps {
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
}
export function CalendarView({
  events,
  onEventClick
}: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  // Adjust for Monday start (Italian standard)
  const startDay = firstDay === 0 ? 6 : firstDay - 1;
  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const today = () => setCurrentDate(new Date());
  const getEventsForDay = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };
  const renderCalendarDays = () => {
    const days = [];
    // Empty cells for previous month
    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-32 bg-slate-50/30 border border-slate-100" data-id="element-65" data-mp="2xg21hlgls9qv"></div>);
    }
    // Days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayEvents = getEventsForDay(day);
      const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
      days.push(<div key={day} className={`h-32 border border-slate-100 p-2 overflow-hidden hover:bg-slate-50 transition-colors ${isToday ? 'bg-blue-50/30' : 'bg-white'}`} data-id="element-66" data-mp="381jyxv02zktc">
          <div className="flex justify-between items-start mb-1" data-id="element-67" data-mp="3h3jjgxab1711">
            <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full ${isToday ? 'bg-seform-orange text-white' : 'text-slate-700'}`} data-id="element-68" data-mp="3h0nkd5mq0cb0">
              {day}
            </span>
            {dayEvents.length > 0 && <span className="text-[10px] text-slate-400 font-medium" data-id="element-69" data-mp="3vspq36bh1ziy">
                {dayEvents.length} eventi
              </span>}
          </div>
          <div className="space-y-1 overflow-y-auto max-h-[calc(100%-2rem)] custom-scrollbar" data-id="element-70" data-mp="8nzsy610q2po">
            {dayEvents.map(event => <button key={event.id} onClick={() => onEventClick(event)} className={`
                  w-full text-left text-xs px-2 py-1.5 rounded border truncate transition-all hover:scale-[1.02] font-medium
                  ${event.priority === 'high' ? 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200' : event.priority === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-100 hover:bg-yellow-100' : 'bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100'}
                `} data-id="element-71" data-mp="dmkrm10mgec0">
                <span className="font-bold mr-1" data-id="element-72" data-mp="11io5aynozc0p">
                  {event.type.substring(0, 3)}
                </span>
                {event.title}
              </button>)}
          </div>
        </div>);
    }
    return days;
  };
  return <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-73" data-mp="sni4ke3a3k51">
      {/* Calendar Header */}
      <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50" data-id="element-74" data-mp="1kfz4kj9bq5g5">
        <div className="flex items-center gap-4" data-id="element-75" data-mp="14xy0l81of7t8">
          <h2 className="text-xl font-bold text-seform-navy capitalize" data-id="element-76" data-mp="150kahec1hzem">
            {monthNames[month]} {year}
          </h2>
          <div className="flex items-center bg-white rounded-lg border border-slate-200 p-1 shadow-sm" data-id="element-77" data-mp="vxsyndf951mg">
            <button onClick={prevMonth} className="p-1 hover:bg-slate-100 rounded-md transition-colors" data-id="element-78" data-mp="zz4n3fme3gt2">
              <ChevronLeft className="h-5 w-5 text-slate-600" data-id="element-79" data-mp="1npik7kju05ui" />
            </button>
            <button onClick={today} className="px-3 py-1 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-md transition-colors" data-id="element-80" data-mp="1sn0j0i44x4os">
              Oggi
            </button>
            <button onClick={nextMonth} className="p-1 hover:bg-slate-100 rounded-md transition-colors" data-id="element-81" data-mp="1iwfnj3jrcga3">
              <ChevronRight className="h-5 w-5 text-slate-600" data-id="element-82" data-mp="2bebab0drvjh7" />
            </button>
          </div>
        </div>
        <div className="flex gap-3 text-sm" data-id="element-83" data-mp="3w46vljtnz539">
          <div className="flex items-center gap-1.5" data-id="element-84" data-mp="3w2vr1hse9tb3">
            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-85" data-mp="3r6ngkhp56oa9"></div>
            <span className="text-slate-600" data-id="element-86" data-mp="9pdzsnxpmr2p">Alta</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-87" data-mp="9om297kjyj9s">
            <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-88" data-mp="4q12f997q1lv"></div>
            <span className="text-slate-600" data-id="element-89" data-mp="3d64c1kjvy0cu">Media</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-90" data-mp="3d6cojbc3j3ni">
            <div className="w-3 h-3 rounded-full bg-blue-500" data-id="element-91" data-mp="387t2laxi5rv5"></div>
            <span className="text-slate-600" data-id="element-92" data-mp="3n4abzwm4swf2">Bassa</span>
          </div>
        </div>
      </div>

      {/* Days Header */}
      <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/30" data-id="element-93" data-mp="3i4nxzkyhbc57">
        {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => <div key={day} className="py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-94" data-mp="12g5rp97qstk8">
            {day}
          </div>)}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 bg-slate-100 gap-px border-b border-l border-slate-100" data-id="element-95" data-mp="19gq4w4zhh6dj">
        {renderCalendarDays()}
      </div>
    </div>;
}